import React, { useMemo } from 'react';
import { AccountSelectModal } from '../account';
import config from '../../config/config';
import { useLocation, useParams } from 'react-router';
import { BasicAccountFragment, useGetAccountLazyQuery, useGetCurrentUserQuery } from '../../generated/types';
import { useAsync } from 'react-use';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { User } from '@menuzen/icon/build/outline';
import PlanUpgradeButton from '../grow-plan/PlanUpgradeButton';
import { AccountUpgradeModal } from '../grow-plan/AccountUpgradeModal';
import { useQueryParam } from '../../common/hooks/use-query-param';
import { MODAL } from '../../pages/site-design/modals/config';
import { useCanUpgrade, Account } from '@mnz/builder';
import { PlanUpgradeSuccessModal } from '../grow-plan/PlanUpgradeSuccessModal';

const MODAL_SWITCH_ACCOUNT = 'SWITCH_ACCOUNT';

const BaseHeader: React.FC = () => {
    const { accountId } = useParams<{ accountId: string }>();
    const [modal, setModal] = useQueryParam<string | null>('modal');
    const { pathname, search } = useLocation();
    const onSelectAccount = (account: BasicAccountFragment) => {
        const pathArray = pathname.split('/');
        pathArray[2] = String(account.id);
        const newPath = pathArray.join('/');
        const params = new URLSearchParams(search);
        params.delete('modal');
        const newSearch = params.toString() ? `?${params.toString()}` : '';
        window.location.href = process.env.REACT_APP_ENDPOINT + newPath + newSearch;
    };
    const [getAccount, { data, loading: isLoadingAccount }] = useGetAccountLazyQuery();
    const { data: userData, loading: isLoadingUser } = useGetCurrentUserQuery();
    useAsync(async () => {
        accountId &&
            (await getAccount({
                variables: {
                    id: Number(accountId),
                },
            }));
    }, [accountId]);
    const { account } = data || {};

    const onClickProfile = () => {
        const uri = window.encodeURIComponent(`${config.appUrl}/account/${accountId}/home`);
        window.location.href = `${config.authUrl}/profile?redirect_uri=${uri}`;
    };
    const canUpgrade = useCanUpgrade({ account: account as Account });

    const successUrl = useMemo(() => {
        const theUrl = new URL(window.location.href);
        theUrl.searchParams.set('modal', MODAL.UPGRADE_ACCOUNT_SUCCESS);
        return theUrl.href;
    }, [modal]);

    return (
        <>
            <div className="flex bg-white justify-between px-28 py-12 item-centers text-14">
                <div className={`flex tems-center space-x-6`}>
                    {!isLoadingUser && userData?.currentUser ? (
                        <div className={`cursor-pointer`} onClick={onClickProfile}>
                            {userData?.currentUser && userData?.currentUser.picture ? (
                                <div className="w-34 h-34 rounded-circle flex items-center justify-center overflow-hidden">
                                    <img src={userData?.currentUser.picture} />
                                </div>
                            ) : (
                                <div className="w-34 h-34 rounded-circle flex items-center justify-center bg-white border border-primary-40 text-16 text-success-dark">
                                    <User />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="w-34 h-34 rounded-circle flex items-center justify-center bg-white border border-primary-40 text-16 text-success-dark">
                            <User />
                        </div>
                    )}
                    {!isLoadingAccount && account && (
                        <div
                            className={`flex items-center cursor-pointer border border-primary-40 rounded-round px-14 py-7`}
                            onClick={() => {
                                setModal(MODAL_SWITCH_ACCOUNT);
                            }}
                        >
                            <span className={`pr-5 text-12 font-bold`}>{account.name}</span>
                            <div className={`flex items-center justify-center text-12`}>
                                <FontAwesomeIcon icon={faAngleDown} />
                            </div>
                        </div>
                    )}
                </div>
                {!isLoadingAccount && account && canUpgrade && (
                    <PlanUpgradeButton onClick={() => setModal(MODAL.UPGRADE_ACCOUNT)} />
                )}
            </div>
            <AccountSelectModal
                onSelectAccount={(account) => {
                    setModal(null);
                    onSelectAccount(account);
                }}
                open={modal == MODAL_SWITCH_ACCOUNT}
                onClose={() => setModal(null)}
                title="Switch Business"
                showClose={true}
            />
            <AccountUpgradeModal
                open={modal === MODAL.UPGRADE_ACCOUNT}
                anchor={`bottom`}
                onClose={() => setModal(null)}
                showClose={true}
                showBarIcon={false}
                cancelUrl={window.location.href}
                successUrl={successUrl}
            />
            <PlanUpgradeSuccessModal
                open={modal === MODAL.UPGRADE_ACCOUNT_SUCCESS}
                anchor={`bottom`}
                onClose={() => setModal(null)}
                showClose={true}
                showBarIcon={false}
            />
        </>
    );
};

export default BaseHeader;
